import expandSelectionToWordBoundaries from 'utils/expandSelectionToWordBoundaries';

function getInnermostCode(node) {
  let n = node?.nodeType === Node.ELEMENT_NODE ? node : node?.parentElement;
  while (n) {
    if ((n.tagName || '').toLowerCase() === 'code') return n;
    n = n.parentElement;
  }
  return null;
}

function unwrapCode(codeEl, selection) {
  const parent = codeEl.parentNode;
  if (!parent) return;
  const fragment = document.createDocumentFragment();
  while (codeEl.firstChild) fragment.appendChild(codeEl.firstChild);
  const next = codeEl.nextSibling;
  parent.removeChild(codeEl);
  parent.insertBefore(fragment, next);
  const first = fragment.firstChild;
  const r = document.createRange();
  if (first) {
    r.setStart(first, 0);
    r.collapse(true);
  } else {
    r.setStart(parent, 0);
    r.collapse(true);
  }
  selection.removeAllRanges();
  selection.addRange(r);
}

/**
 * Apply bold / italic / strikethrough / code formatting to the current
 * selection inside a contentEditable element.  Works via execCommand for
 * bold/italic/strike and DOM manipulation for <code>.
 */
export default function applyContentEditableFormat(input, prefix, suffix) {
  if (!input || input.contentEditable !== 'true') return;

  input.focus();
  const sel = window.getSelection();
  if (!sel.rangeCount) return;
  const range = sel.getRangeAt(0);
  let didInsertPadForCollapsed = false;

  if (!range.collapsed && input.contains(range.startContainer)) {
    expandSelectionToWordBoundaries(input, range);
    sel.removeAllRanges();
    sel.addRange(range);
  } else if (range.collapsed && suffix !== '```' && suffix !== '`') {
    didInsertPadForCollapsed = true;
    const pad = document.createTextNode(' \u200B ');
    range.insertNode(pad);
    range.setStart(pad, 1);
    range.setEnd(pad, 2);
    sel.removeAllRanges();
    sel.addRange(range);
  }

  if (suffix === '*') document.execCommand('bold', false, null);
  else if (suffix === '_') document.execCommand('italic', false, null);
  else if (suffix === '~') document.execCommand('strikeThrough', false, null);
  else if (suffix === '```' || suffix === '`') {
    const anchorCode = getInnermostCode(range.startContainer);
    const focusCode = range.collapsed ? anchorCode : getInnermostCode(range.endContainer);
    if (anchorCode && anchorCode === focusCode && input.contains(anchorCode)) {
      unwrapCode(anchorCode, sel);
      input.dispatchEvent(new Event('input', { bubbles: true }));
      return;
    }
    const hasSelection = !range.collapsed;
    if (hasSelection) {
      const code = document.createElement('code');
      try {
        range.surroundContents(code);
      } catch {
        code.appendChild(range.extractContents());
        range.insertNode(code);
      }
      sel.removeAllRanges();
      const r = document.createRange();
      r.setStart(code, 0);
      r.setEnd(code, 0);
      sel.addRange(r);
    } else {
      const code = document.createElement('code');
      code.appendChild(document.createTextNode('\u200B'));
      range.insertNode(code);
      const r = document.createRange();
      r.setStart(code.firstChild, 1);
      r.setEnd(code.firstChild, 1);
      sel.removeAllRanges();
      sel.addRange(r);
    }
  } else if (didInsertPadForCollapsed) {
    const sel2 = window.getSelection();
    if (sel2.rangeCount) {
      const r2 = sel2.getRangeAt(0);
      const node = r2.startContainer;
      if (node.nodeType === Node.TEXT_NODE && node.textContent === '\u200B') {
        r2.setStart(node, 0);
        r2.setEnd(node, 1);
        sel2.removeAllRanges();
        sel2.addRange(r2);
      }
    }
  }

  input.dispatchEvent(new Event('input', { bubbles: true }));
}
